import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const REASONS = [
  ["anomalia", "Segnalare un problema"],
  ["domanda", "Fare una domanda"],
  ["abbonamento", "Abbonamento e pagamenti"],
  ["suggerimento", "Dare un suggerimento"],
  ["altro", "Altro"],
];

const field = "w-full rounded-xl border border-[#27272A] bg-[#121215] px-4 py-3 text-base text-[#FAF9F6] placeholder:text-[#8A8E96] focus:border-[#D4AF37]/60 focus:outline-none";

function ContactForm({ onDone }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", reason: "domanda", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...f, page: pathname });
      onDone();
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
      <select value={f.reason} onChange={set("reason")} className={field} data-testid="contact-reason-select">
        {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <input required value={f.name} onChange={set("name")} placeholder="Nome" maxLength={80} className={field} data-testid="contact-name-input" />
      <input required type="email" value={f.email} onChange={set("email")} placeholder="Email per la risposta" className={field} data-testid="contact-email-input" />
      <textarea required minLength={5} maxLength={3000} rows={5} value={f.message} onChange={set("message")} placeholder="Scrivi qui il tuo messaggio…" className={`${field} resize-none`} data-testid="contact-message-input" />
      <button type="submit" disabled={busy} data-testid="contact-submit-button" className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base uppercase tracking-wide disabled:opacity-50">
        <Send size={16} /> {busy ? "Invio…" : "Invia"}
      </button>
    </form>
  );
}

export function ContactDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const change = (v) => { setOpen(v); if (!v) setSent(false); };
  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      <Dialog open={open} onOpenChange={change}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-[400px] rounded-3xl border-[#27272A] bg-[#09090B] text-[#FAF9F6]" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-el text-2xl">Scrivici</DialogTitle>
            <DialogDescription className="text-[#B4B4BC]">Problemi, domande o suggerimenti: ti rispondiamo via email.</DialogDescription>
          </DialogHeader>
          {sent ? (
            <div className="py-6 text-center" data-testid="contact-success">
              <CheckCircle2 size={40} className="mx-auto text-[#D4AF37]" />
              <p className="mt-3 text-base text-[#E4E4E7]">Messaggio inviato. Ti abbiamo mandato una conferma via email.</p>
            </div>
          ) : (
            <ContactForm onDone={() => setSent(true)} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ContactLink({ testId = "contact-link" }) {
  return (
    <ContactDialog
      trigger={
        <button type="button" data-testid={testId} className="inline-flex items-center gap-1.5 text-sm text-[#8A8E96] underline-offset-4 hover:text-[#D4AF37] hover:underline">
          <Mail size={14} /> Hai bisogno di aiuto? Scrivici
        </button>
      }
    />
  );
}
